import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Car,
  Check,
  CloudSun,
  FileText,
  MapPin,
  Send,
  Trash2,
  Upload,
} from 'lucide-react'
import { AppShell, PageHeader } from '../components/AppShell'
import { Dropzone, EvidenceList } from '../components/Dropzone'
import { EvidencePlayer } from '../components/EvidencePlayer'
import {
  Callout,
  Field,
  Input,
  Panel,
  SectionHeading,
  Select,
  Spinner,
  Textarea,
  Toggle,
} from '../components/ui'
import { api, uploadEvidence } from '../lib/api'
import { usePortal } from '../lib/portal-context'
import { clock, cx, fromLocalInput, toLocalInput } from '../lib/format'
import type { Marker, ViolationCase } from '../lib/types'

const STEPS = [
  { key: 'evidence', label: 'Evidence', icon: Upload },
  { key: 'violation', label: 'Violation', icon: AlertTriangle },
  { key: 'vehicle', label: 'Vehicle', icon: Car },
  { key: 'context', label: 'Context', icon: CloudSun },
  { key: 'review', label: 'Review', icon: FileText },
] as const

const PLATE_STATES = ['CA', 'AZ', 'NV', 'OR', 'WA', 'TX', 'UT', 'ID', 'Other', 'Unknown']

export function NewCase() {
  const { config } = usePortal()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const caseParam = searchParams.get('case')

  const [record, setRecord] = useState<ViolationCase | null>(null)
  const [step, setStep] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [problems, setProblems] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  const pendingPatch = useRef<Record<string, unknown>>({})
  const saveTimer = useRef<number | null>(null)
  const recordRef = useRef<ViolationCase | null>(null)
  recordRef.current = record

  /* --- load or create the working case --- */
  useEffect(() => {
    let cancelled = false

    const boot = async () => {
      try {
        if (caseParam) {
          const payload = await api.getCase(caseParam)
          if (!cancelled) setRecord(payload.case)
          return
        }
        const created = await api.createCase()
        if (cancelled) return
        setRecord(created.case)
        setSearchParams({ case: created.case.caseNumber }, { replace: true })
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Could not open a case.')
        }
      }
    }

    void boot()
    return () => {
      cancelled = true
    }
  }, [caseParam, setSearchParams])

  /* --- debounced autosave --- */
  const flush = useCallback(async () => {
    const current = recordRef.current
    const patch = pendingPatch.current
    if (!current || Object.keys(patch).length === 0) return
    pendingPatch.current = {}
    setSaving(true)
    try {
      const payload = await api.updateCase(current.caseNumber, patch)
      setRecord((local) => {
        if (!local) return payload.case
        const next = {
          ...payload.case,
          evidence: local.evidence.length ? local.evidence : payload.case.evidence,
        }
        return Object.keys(pendingPatch.current).length ? mergeDeep(next, pendingPatch.current) : next
      })
    } catch (cause) {
      pendingPatch.current = mergeDeep(patch, pendingPatch.current)
      setError(cause instanceof Error ? cause.message : 'Autosave failed.')
    } finally {
      setSaving(false)
    }
  }, [])

  const update = useCallback(
    (patch: Record<string, unknown>, immediate = false) => {
      setRecord((current) => (current ? mergeDeep(current, patch) : current))
      pendingPatch.current = mergeDeep(pendingPatch.current, patch)
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
      if (immediate) void flush()
      else saveTimer.current = window.setTimeout(() => void flush(), 600)
    },
    [flush],
  )

  useEffect(
    () => () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
    },
    [],
  )

  const handleFiles = async (files: File[]) => {
    if (!record) return
    setUploading(true)
    setProgress(0)
    setError(null)
    try {
      const payload = await uploadEvidence(record.caseNumber, files, setProgress)
      setRecord(payload.case)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Upload failed.')
    } finally {
      setUploading(false)
      setProgress(0)
    }
  }

  const removeEvidence = async (evidenceId: string) => {
    if (!record) return
    try {
      const payload = await api.deleteEvidence(record.caseNumber, evidenceId)
      setRecord(payload.case)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not remove that file.')
    }
  }

  const setCaption = async (evidenceId: string, caption: string) => {
    if (!record) return
    const payload = await api.updateCase(record.caseNumber, {
      evidenceMeta: [{ id: evidenceId, caption }],
    })
    setRecord(payload.case)
  }

  const setSource = async (evidenceId: string, source: string) => {
    if (!record) return
    const payload = await api.updateCase(record.caseNumber, {
      evidenceMeta: [{ id: evidenceId, source }],
    })
    setRecord(payload.case)
  }

  const setCaptureDevice = (value: string) => {
    if (!record) return
    const previous = record.captureDevice
    setRecord((current) => {
      if (!current) return current
      return {
        ...current,
        captureDevice: value,
        evidence: current.evidence.map((item) =>
          item.kind === 'video' && (!item.source || item.source === previous)
            ? { ...item, source: value }
            : item,
        ),
      }
    })
    pendingPatch.current = mergeDeep(pendingPatch.current, { captureDevice: value })
    if (saveTimer.current) window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(() => void flush(), 600)
  }

  const reorderEvidence = (ids: string[]) => {
    if (!record) return
    const byId = new Map(record.evidence.map((item) => [item.id, item]))
    const leftover = record.evidence.filter((item) => !ids.includes(item.id))
    const ordered = [...ids.map((id) => byId.get(id)).filter(Boolean), ...leftover]
    setRecord((current) => (current ? { ...current, evidence: ordered as ViolationCase['evidence'] } : current))
    pendingPatch.current = mergeDeep(pendingPatch.current, { evidenceOrder: ids })
    if (saveTimer.current) window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(() => void flush(), 400)
  }

  const addMarker = (evidenceId: string, timeSec: number) => {
    if (!record) return
    const marker: Marker = {
      id: `m-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      evidenceId,
      timeSec: Number(timeSec.toFixed(2)),
      label: '',
    }
    update({ markers: [...record.markers, marker] })
  }

  const updateMarker = (id: string, label: string) => {
    if (!record) return
    update({
      markers: record.markers.map((marker) => (marker.id === id ? { ...marker, label } : marker)),
    })
  }

  const removeMarker = (id: string) => {
    if (!record) return
    update({ markers: record.markers.filter((marker) => marker.id !== id) })
  }

  const submit = async () => {
    if (!record) return
    setSubmitting(true)
    setProblems([])
    setError(null)
    try {
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
      await flush()
      if (record.status !== 'draft') {
        navigate(`/case/${record.caseNumber}-${record.shareToken}`)
        return
      }
      const payload = await api.submitCase(record.caseNumber)
      navigate(`/case/${payload.case.caseNumber}-${payload.case.shareToken}?share=1`)
    } catch (cause) {
      const apiError = cause as { problems?: string[]; message?: string }
      if (apiError.problems?.length) {
        setProblems(apiError.problems)
        setStep(4)
      } else {
        setError(apiError.message || 'Could not submit the case.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const goToStep = async (next: number) => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current)
    await flush()
    setStep(next)
  }

  const continueNext = async () => {
    if (!record) return
    const issues = problemsForStep(record, step)
    if (issues.length) {
      setProblems(issues)
      return
    }
    setProblems([])
    await goToStep(Math.min(STEPS.length - 1, step + 1))
  }

  const discardDraft = async () => {
    if (!record) return
    setDiscarding(true)
    setError(null)
    try {
      if (saveTimer.current) window.clearTimeout(saveTimer.current)
      pendingPatch.current = {}
      await api.deleteCase(record.caseNumber)
      navigate('/')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not discard the draft.')
      setDiscarding(false)
      setConfirmDiscard(false)
    }
  }

  const isSubmitted = !!record && record.status !== 'draft'
  const isEmptyDraft =
    !!record &&
    !isSubmitted &&
    !record.evidence.length &&
    !record.violations.length &&
    !record.narrative &&
    !record.occurredAt

  const videos = useMemo(
    () => (record?.evidence ?? []).filter((item) => item.kind === 'video'),
    [record?.evidence],
  )

  if (error && !record) {
    return (
      <AppShell>
        <div className="mx-auto max-w-2xl px-4 py-10">
          <Callout tone="danger" title="Could not open the case">
            {error}
          </Callout>
        </div>
      </AppShell>
    )
  }

  if (!record) {
    return (
      <AppShell>
        <div className="flex items-center justify-center gap-2 py-24 text-[13.5px] text-slate-400">
          <Spinner /> Opening a new case…
        </div>
      </AppShell>
    )
  }

  const violations = config?.catalog.violations ?? []
  const groups = [...new Set(violations.map((type) => type.group))]

  return (
    <AppShell
      actions={
        <>
          <span className="hidden items-center gap-1.5 text-[12px] text-slate-500 sm:flex">
            {saving ? (
              <>
                <Spinner size={12} /> Saving
              </>
            ) : (
              <>
                <Check size={13} className="text-emerald-400" /> Draft saved
              </>
            )}
          </span>
          <span className="font-mono text-[12.5px] font-semibold text-altair-400">
            {record.caseNumber}
          </span>
          {!isSubmitted ? (
            <button
              type="button"
              onClick={() => setConfirmDiscard(true)}
              disabled={discarding}
              className="btn-subtle px-2.5 py-1.5 text-[12.5px] text-slate-500 hover:text-red-300"
            >
              <Trash2 size={14} />
              <span className="hidden sm:inline">Discard draft</span>
            </button>
          ) : null}
        </>
      }
    >
      <div className="mx-auto max-w-[1100px] px-4 py-7 sm:px-6">
        {confirmDiscard ? (
          <div className="mb-5 rounded-lg border border-red-500/25 bg-red-500/[0.07] px-4 py-3">
            <p className="text-[13px] text-red-100">
              {isEmptyDraft
                ? `Discard empty draft ${record.caseNumber}?`
                : `Discard draft ${record.caseNumber}${
                    record.evidence.length
                      ? ` and delete ${record.evidence.length} uploaded file${
                          record.evidence.length === 1 ? '' : 's'
                        }`
                      : ''
                  }? This cannot be undone.`}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void discardDraft()}
                disabled={discarding}
                className="btn-danger"
              >
                {discarding ? <Spinner /> : <Trash2 size={14} />}
                Discard draft
              </button>
              <button
                type="button"
                onClick={() => setConfirmDiscard(false)}
                disabled={discarding}
                className="btn-ghost"
              >
                Keep editing
              </button>
            </div>
          </div>
        ) : null}

        <PageHeader
          eyebrow={isSubmitted ? 'Edit submitted report' : 'New violation report'}
          title={isSubmitted ? 'Update the report' : 'Document the violation'}
          blurb={
            isSubmitted
              ? 'Add photos or video, fix details, and reorder evidence. Changes save as you go and stay on the same case link.'
              : 'Everything you add here becomes a single report with a permanent case link. Upload the evidence first — the rest can be filled in around it.'
          }
        />

        {/* Step rail */}
        <div className="no-print mb-6 flex flex-wrap items-center gap-1.5 rounded-xl border border-white/[0.07] bg-ink-900/60 p-1.5">
          {STEPS.map((entry, index) => {
            const Icon = entry.icon
            const done = index < step
            return (
              <button
                key={entry.key}
                type="button"
                onClick={() => {
                  if (index > step) {
                    const issues = problemsForStep(record, step)
                    if (issues.length) {
                      setProblems(issues)
                      return
                    }
                    setProblems([])
                  }
                  void goToStep(index)
                }}
                aria-label={`Step ${index + 1}: ${entry.label}`}
                aria-current={index === step ? 'step' : undefined}
                className={cx(
                  'flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-medium transition',
                  index === step
                    ? 'bg-altair-500 text-white shadow-[0_6px_20px_-8px_rgba(239,78,35,0.8)]'
                    : done
                      ? 'text-emerald-300 hover:bg-white/[0.05]'
                      : 'text-slate-400 hover:bg-white/[0.05] hover:text-slate-200',
                )}
              >
                {done && index !== step ? <Check size={15} /> : <Icon size={15} />}
                <span className="hidden sm:inline">{entry.label}</span>
              </button>
            )
          })}
        </div>

        {error ? (
          <Callout tone="danger" className="mb-5">
            {error}
          </Callout>
        ) : null}

        {problems.length > 0 && step !== 4 ? (
          <Callout tone="warn" title="A few things are still needed" className="mb-5">
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </Callout>
        ) : null}

        {/* ---------------- Step 1: evidence ---------------- */}
        {step === 0 ? (
          <div className="space-y-5">
            <Panel>
              <SectionHeading
                step={1}
                title="Upload the evidence"
                blurb="Dashcam clips, phone video, and still photos. The original files are stored unmodified with a SHA-256 checksum so their integrity can be verified later."
              />
              <Field
                label="Capture device"
                hint="Shown on each video. Phone, dashcam, doorbell — whatever recorded it."
                className="mb-4"
              >
                <Input
                  value={record.captureDevice}
                  onChange={(event) => setCaptureDevice(event.target.value)}
                  placeholder="Dashcam"
                />
              </Field>
              <Dropzone onFiles={handleFiles} uploading={uploading} progress={progress} />
              {record.evidence.length ? (
                <div className="mt-4">
                  <div className="label">Attached files ({record.evidence.length})</div>
                  <EvidenceList
                    caseId={record.id}
                    evidence={record.evidence}
                    onRemove={removeEvidence}
                    onCaption={setCaption}
                    onSource={setSource}
                    onReorder={reorderEvidence}
                    onFiles={handleFiles}
                  />
                  <p className="mt-2 text-[11.5px] text-slate-500">
                    Drag files to put them in chronological order. Dropping a new clip onto this list
                    also uploads it. That order is what the report uses.
                  </p>
                </div>
              ) : null}
            </Panel>

            {videos.map((video) => (
              <Panel
                key={video.id}
                title={`Flag key moments · ${video.originalName}`}
                bodyClassName="space-y-4"
              >
                <p className="text-[13px] leading-relaxed text-slate-400">
                  Scrub to the moment that matters and press <strong className="text-slate-200">Mark
                  moment</strong>. Markers show up on the timeline of the report so the office manager
                  can jump straight to it instead of hunting through the clip.
                </p>
                <EvidencePlayer
                  caseId={record.id}
                  evidence={video}
                  markers={record.markers}
                  occurredAt={record.occurredAt}
                  onAddMarker={(time) => addMarker(video.id, time)}
                />
                {record.markers.filter((marker) => marker.evidenceId === video.id).length ? (
                  <div className="space-y-2">
                    {record.markers
                      .filter((marker) => marker.evidenceId === video.id)
                      .sort((a, b) => a.timeSec - b.timeSec)
                      .map((marker) => (
                        <div key={marker.id} className="flex items-center gap-2">
                          <span className="w-14 shrink-0 rounded bg-amber-400/12 px-2 py-1.5 text-center font-mono text-[12px] text-amber-300">
                            {clock(marker.timeSec)}
                          </span>
                          <input
                            autoFocus={!marker.label}
                            defaultValue={marker.label}
                            onBlur={(event) => updateMarker(marker.id, event.target.value)}
                            placeholder="What happens here? e.g. enters intersection without stopping"
                            className="input input-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeMarker(marker.id)}
                            className="btn-subtle shrink-0 p-2 hover:text-red-300"
                            aria-label="Remove marker"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                  </div>
                ) : null}
              </Panel>
            ))}
          </div>
        ) : null}

        {/* ---------------- Step 2: violation ---------------- */}
        {step === 1 ? (
          <div className="space-y-5">
            <Panel>
              <SectionHeading
                step={2}
                title="What happened"
                blurb="Pick everything that applies. The California Vehicle Code reference for each is included in the report for context."
              />
              <div className="space-y-5">
                {groups.map((group) => (
                  <div key={group}>
                    <div className="label">{group}</div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {violations
                        .filter((type) => type.group === group)
                        .map((type) => {
                          const active = record.violations.includes(type.code)
                          return (
                            <button
                              key={type.code}
                              type="button"
                              onClick={() => {
                                const next = active
                                  ? record.violations.filter((code) => code !== type.code)
                                  : [...record.violations, type.code]
                                update({
                                  violations: next,
                                  ...(next.length === 1 && !active
                                    ? { severity: type.defaultSeverity }
                                    : {}),
                                })
                              }}
                              className={cx(
                                'rounded-lg border px-3.5 py-3 text-left transition',
                                active
                                  ? 'border-altair-500/50 bg-altair-500/[0.09]'
                                  : 'border-white/[0.08] bg-white/[0.02] hover:border-white/20',
                              )}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <span
                                  className={cx(
                                    'text-[13.5px] font-semibold leading-snug',
                                    active ? 'text-white' : 'text-slate-200',
                                  )}
                                >
                                  {type.label}
                                </span>
                                {active ? (
                                  <Check size={15} className="mt-0.5 shrink-0 text-altair-400" />
                                ) : null}
                              </div>
                              <p className="mt-1 text-[11.5px] leading-snug text-slate-500">
                                {type.blurb}
                              </p>
                              {type.cvc ? (
                                <p className="mt-1.5 font-mono text-[10.5px] text-slate-600">
                                  {type.cvc}
                                </p>
                              ) : null}
                            </button>
                          )
                        })}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Severity, timing and speed" bodyClassName="space-y-4">
              <div>
                <div className="label">How serious was it?</div>
                <div className="grid gap-2 sm:grid-cols-3">
                  {(config?.catalog.severities ?? []).map((severity) => (
                    <button
                      key={severity.code}
                      type="button"
                      onClick={() => update({ severity: severity.code })}
                      className={cx(
                        'rounded-lg border px-3.5 py-3 text-left transition',
                        record.severity === severity.code
                          ? 'border-altair-500/50 bg-altair-500/[0.09] text-white'
                          : 'border-white/[0.08] bg-white/[0.02] text-slate-300 hover:border-white/20',
                      )}
                    >
                      <div className="text-[13.5px] font-semibold">{severity.label}</div>
                      <p className="mt-1 text-[11.5px] leading-snug text-slate-500">
                        {severity.blurb}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Date and time of violation" required className="sm:col-span-1">
                  <Input
                    type="datetime-local"
                    value={toLocalInput(record.occurredAt)}
                    onChange={(event) => {
                      const value = event.target.value
                      if (!value) return
                      const next = fromLocalInput(value)
                      if (next) update({ occurredAt: next }, true)
                    }}
                  />
                </Field>
                <Field label="Estimated speed (mph)" hint="Leave blank if not applicable.">
                  <Input
                    type="number"
                    min={0}
                    max={140}
                    value={record.estimatedSpeed ?? ''}
                    onChange={(event) =>
                      update({ estimatedSpeed: event.target.value === '' ? null : Number(event.target.value) })
                    }
                    placeholder="45"
                  />
                </Field>
                <Field label="Posted limit (mph)">
                  <Input
                    type="number"
                    min={0}
                    max={80}
                    value={record.postedSpeed ?? ''}
                    onChange={(event) =>
                      update({ postedSpeed: event.target.value === '' ? null : Number(event.target.value) })
                    }
                    placeholder="25"
                  />
                </Field>
              </div>
            </Panel>

            <Panel title="Where it happened" icon={<MapPin size={14} />} bodyClassName="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Street" required hint="Inside the community.">
                  <Input
                    list="known-locations"
                    value={record.location.street}
                    onChange={(event) => update({ location: { street: event.target.value } })}
                    placeholder="Altair Pkwy"
                  />
                  <datalist id="known-locations">
                    {(config?.catalog.knownLocations ?? []).map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                </Field>
                <Field label="Cross street or intersection">
                  <Input
                    value={record.location.crossStreet}
                    onChange={(event) => update({ location: { crossStreet: event.target.value } })}
                    placeholder="Trailblazer"
                  />
                </Field>
                <Field label="Nearest address or landmark">
                  <Input
                    value={record.location.nearestAddress}
                    onChange={(event) => update({ location: { nearestAddress: event.target.value } })}
                    placeholder="In front of the park / near 104"
                  />
                </Field>
                <Field label="Location notes" hint="Sight lines, signage, crosswalk, school route.">
                  <Input
                    value={record.location.notes}
                    onChange={(event) => update({ location: { notes: event.target.value } })}
                    placeholder="Stop sign is clearly posted and unobstructed"
                  />
                </Field>
              </div>
            </Panel>
          </div>
        ) : null}

        {/* ---------------- Step 3: vehicle ---------------- */}
        {step === 2 ? (
          <div className="space-y-5">
            <Panel>
              <SectionHeading
                step={3}
                title="Vehicle description"
                blurb="The plate is what lets management match the vehicle to a resident or a guest pass. Fill in whatever is legible in the footage."
              />
              <div className="grid gap-4 sm:grid-cols-4">
                <Field label="License plate" className="sm:col-span-2">
                  <Input
                    value={record.vehicle.plate}
                    onChange={(event) =>
                      update({ vehicle: { plate: event.target.value.toUpperCase() } })
                    }
                    placeholder="8ABC123"
                    className="font-mono tracking-[0.15em]"
                  />
                </Field>
                <Field label="State">
                  <Select
                    value={record.vehicle.plateState}
                    onChange={(event) => update({ vehicle: { plateState: event.target.value } })}
                  >
                    {PLATE_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Color">
                  <Input
                    value={record.vehicle.color}
                    onChange={(event) => update({ vehicle: { color: event.target.value } })}
                    placeholder="Black"
                  />
                </Field>
                <Field label="Year">
                  <Input
                    value={record.vehicle.year}
                    onChange={(event) => update({ vehicle: { year: event.target.value } })}
                    placeholder="2021"
                  />
                </Field>
                <Field label="Make">
                  <Input
                    value={record.vehicle.make}
                    onChange={(event) => update({ vehicle: { make: event.target.value } })}
                    placeholder="BMW"
                  />
                </Field>
                <Field label="Model">
                  <Input
                    value={record.vehicle.model}
                    onChange={(event) => update({ vehicle: { model: event.target.value } })}
                    placeholder="M3"
                  />
                </Field>
                <Field label="Body style">
                  <Input
                    value={record.vehicle.bodyStyle}
                    onChange={(event) => update({ vehicle: { bodyStyle: event.target.value } })}
                    placeholder="Sedan"
                  />
                </Field>
                <Field
                  label="Distinguishing details"
                  className="sm:col-span-4"
                  hint="Damage, decals, rims, roof rack, paper plates, dealer frame — anything that identifies this specific vehicle."
                >
                  <Input
                    value={record.vehicle.distinguishing}
                    onChange={(event) => update({ vehicle: { distinguishing: event.target.value } })}
                    placeholder="Aftermarket black rims, dealer plate frame, tinted windows"
                  />
                </Field>
              </div>
            </Panel>

            <Panel title="Resident association" bodyClassName="space-y-4">
              <p className="text-[13px] leading-relaxed text-slate-400">
                Only fill this in if you actually know it. Management can match the plate themselves —
                guessing at a neighbor's identity weakens the report.
              </p>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Is the driver a resident?">
                  <Select
                    value={record.resident.knownResident}
                    onChange={(event) => update({ resident: { knownResident: event.target.value } })}
                  >
                    <option value="unknown">Unknown</option>
                    <option value="resident">Believed to be a resident</option>
                    <option value="guest">Guest or visitor</option>
                    <option value="delivery">Delivery / service vehicle</option>
                    <option value="outside">Non-resident cutting through</option>
                  </Select>
                </Field>
                <Field label="Name (if known)">
                  <Input
                    value={record.resident.name}
                    onChange={(event) => update({ resident: { name: event.target.value } })}
                    placeholder="Leave blank if unsure"
                  />
                </Field>
                <Field label="Unit / address (if known)">
                  <Input
                    value={record.resident.unit}
                    onChange={(event) => update({ resident: { unit: event.target.value } })}
                    placeholder="Leave blank if unsure"
                  />
                </Field>
              </div>
            </Panel>
          </div>
        ) : null}

        {/* ---------------- Step 4: context ---------------- */}
        {step === 3 ? (
          <div className="space-y-5">
            <Panel>
              <SectionHeading
                step={4}
                title="Describe what happened"
                blurb="Write it factually and in order, as if the reader has not seen the video yet. This is the part the board reads first."
              />
              <Textarea
                value={record.narrative}
                onChange={(event) => update({ narrative: event.target.value })}
                className="min-h-[180px]"
                placeholder={
                  'At approximately 4:15 PM I was traveling east on Altair Pkwy approaching the four-way stop at Trailblazer. A black BMW sedan approached from the opposite direction and proceeded through the posted stop sign without slowing, crossing directly in front of my vehicle. Two children were on the sidewalk at the northeast corner at the time. My dashcam recorded the entire sequence; the vehicle does not brake at any point.'
                }
              />
              <p className="mt-2 text-[11.5px] text-slate-500">
                {record.narrative.length} characters
              </p>
            </Panel>

            <Panel title="Conditions at the time" icon={<CloudSun size={14} />} bodyClassName="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Weather">
                  <Select
                    value={record.conditions.weather}
                    onChange={(event) => update({ conditions: { weather: event.target.value } })}
                  >
                    <option value="">Not noted</option>
                    {(config?.catalog.conditions.weather ?? []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Lighting">
                  <Select
                    value={record.conditions.lighting}
                    onChange={(event) => update({ conditions: { lighting: event.target.value } })}
                  >
                    <option value="">Not noted</option>
                    {(config?.catalog.conditions.lighting ?? []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Traffic">
                  <Select
                    value={record.conditions.traffic}
                    onChange={(event) => update({ conditions: { traffic: event.target.value } })}
                  >
                    <option value="">Not noted</option>
                    {(config?.catalog.conditions.traffic ?? []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <div className="grid gap-2 sm:grid-cols-3">
                <Toggle
                  checked={record.conditions.pedestriansPresent}
                  onChange={(value) => update({ conditions: { pedestriansPresent: value } })}
                  label="Pedestrians present"
                />
                <Toggle
                  checked={record.conditions.childrenPresent}
                  onChange={(value) => update({ conditions: { childrenPresent: value } })}
                  label="Children present"
                  hint="Raises severity considerably."
                />
                <Toggle
                  checked={record.conditions.schoolZone}
                  onChange={(value) => update({ conditions: { schoolZone: value } })}
                  label="Near park or school route"
                />
              </div>
            </Panel>

            <Panel title="What are you asking management to do?" bodyClassName="space-y-2">
              <div className="grid gap-2 sm:grid-cols-2">
                {(config?.catalog.requestedActions ?? []).map((action) => (
                  <Toggle
                    key={action.code}
                    checked={record.requestedActions.includes(action.code)}
                    onChange={(value) =>
                      update({
                        requestedActions: value
                          ? [...record.requestedActions, action.code]
                          : record.requestedActions.filter((code) => code !== action.code),
                      })
                    }
                    label={action.label}
                  />
                ))}
              </div>
            </Panel>

            <Panel title="Background and reporter details" bodyClassName="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Prior incidents"
                  hint="Repeat behavior is what usually moves the board to act."
                >
                  <Textarea
                    value={record.priorIncidents}
                    onChange={(event) => update({ priorIncidents: event.target.value })}
                    className="min-h-[80px]"
                    placeholder="Same vehicle observed speeding on 6/14 and 7/02."
                  />
                </Field>
                <Field label="Witnesses">
                  <Textarea
                    value={record.witnesses}
                    onChange={(event) => update({ witnesses: event.target.value })}
                    className="min-h-[80px]"
                    placeholder="Name and contact of anyone else who saw it."
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Your name">
                  <Input
                    value={record.reporter.name}
                    onChange={(event) => update({ reporter: { name: event.target.value } })}
                  />
                </Field>
                <Field label="Your address / unit">
                  <Input
                    value={record.reporter.address}
                    onChange={(event) => update({ reporter: { address: event.target.value } })}
                  />
                </Field>
                <Field label="Email">
                  <Input
                    type="email"
                    value={record.reporter.email}
                    onChange={(event) => update({ reporter: { email: event.target.value } })}
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={record.reporter.phone}
                    onChange={(event) => update({ reporter: { phone: event.target.value } })}
                  />
                </Field>
              </div>

              <Field label="Capture device" hint="Appears on the report as the evidence source.">
                <Input
                  value={record.captureDevice}
                  onChange={(event) => setCaptureDevice(event.target.value)}
                  placeholder="Dashcam"
                />
              </Field>

              <Toggle
                checked={record.reporter.anonymousToResident}
                onChange={(value) => update({ reporter: { anonymousToResident: value } })}
                label="Withhold my identity from the resident"
                hint="Management still sees who filed it; the notice to the resident does not name you."
              />
            </Panel>
          </div>
        ) : null}

        {/* ---------------- Step 5: review ---------------- */}
        {step === 4 ? (
          <div className="space-y-5">
            <Panel>
              <SectionHeading
                step={5}
                title="Review and generate the report"
                blurb={
                  isSubmitted
                    ? 'Changes are already saved. Return to the report when you are done, or keep editing any step.'
                    : 'Submitting assigns the permanent case link you can paste into an email to management. You can keep editing afterward — including adding photos and video.'
                }
              />

              {problems.length ? (
                <Callout tone="warn" title="A few things are still missing" className="mb-4">
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {problems.map((problem) => (
                      <li key={problem}>{problem}</li>
                    ))}
                  </ul>
                </Callout>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <ReviewBlock label="Case number" value={record.caseNumber} mono />
                <ReviewBlock
                  label="Violations"
                  value={
                    record.violations
                      .map((code) => config?.catalog.violations.find((type) => type.code === code)?.label)
                      .filter(Boolean)
                      .join(', ') || 'None selected'
                  }
                />
                <ReviewBlock
                  label="When"
                  value={record.occurredAt ? new Date(record.occurredAt).toLocaleString() : 'Not set'}
                />
                <ReviewBlock
                  label="Where"
                  value={
                    [record.location.street, record.location.crossStreet].filter(Boolean).join(' at ') ||
                    'Not set'
                  }
                />
                <ReviewBlock label="Plate" value={record.vehicle.plate || 'Not legible'} mono />
                <ReviewBlock
                  label="Evidence"
                  value={`${record.evidence.filter((item) => item.kind === 'video').length} video, ${
                    record.evidence.filter((item) => item.kind === 'photo').length
                  } photo, ${record.markers.length} key moment${record.markers.length === 1 ? '' : 's'}`}
                />
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <button type="button" onClick={submit} disabled={submitting} className="btn-primary">
                  {submitting ? <Spinner /> : <Send size={16} />}
                  {submitting
                    ? isSubmitted
                      ? 'Saving…'
                      : 'Generating…'
                    : isSubmitted
                      ? 'Save & return to report'
                      : 'Generate report & get link'}
                </button>
                <span className="text-[12.5px] text-slate-500">
                  Next you can email it to {config?.management ?? 'management'} in one click.
                </span>
              </div>
            </Panel>
          </div>
        ) : null}

        {/* Step nav */}
        <div className="no-print mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => void goToStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="btn-ghost"
          >
            <ArrowLeft size={15} />
            Back
          </button>

          {step < STEPS.length - 1 ? (
            <button type="button" onClick={() => void continueNext()} className="btn-primary">
              Continue
              <ArrowRight size={15} />
            </button>
          ) : (
            <span className="text-[12.5px] text-slate-500">Draft autosaves as you type.</span>
          )}
        </div>
      </div>
    </AppShell>
  )
}

function problemsForStep(record: ViolationCase, currentStep: number) {
  if (currentStep !== 1) return []
  const issues: string[] = []
  if (!record.violations.length) issues.push('Select at least one violation type.')
  if (!record.occurredAt) issues.push('Set the date and time of the violation.')
  if (!record.location?.street?.trim()) issues.push('Enter the street or location.')
  return issues
}

function ReviewBlock({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-white/[0.02] px-3.5 py-3">
      <div className="label mb-1">{label}</div>
      <div className={cx('text-[13.5px] font-medium text-slate-100', mono && 'font-mono tracking-wide')}>
        {value}
      </div>
    </div>
  )
}

/** Shallow-merges one level of nested objects, which is all the case shape needs. */
function mergeDeep<T extends object>(base: T, patch: Record<string, unknown>): T {
  const output: Record<string, unknown> = { ...(base as Record<string, unknown>) }
  for (const [key, value] of Object.entries(patch)) {
    const existing = output[key]
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      existing &&
      typeof existing === 'object' &&
      !Array.isArray(existing)
    ) {
      output[key] = { ...(existing as object), ...(value as object) }
    } else {
      output[key] = value
    }
  }
  return output as T
}
